/**
 * ServerSearchableDropdown - Dropdown with server-side search and pagination
 *
 * Features:
 * - Debounced search (sends requests to backend on filter)
 * - Pagination support (load more on scroll)
 * - Loading states
 * - Match count display
 * - Arrow key navigation
 * - Clear button
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Form, InputGroup, Button, Spinner } from 'react-bootstrap';

interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
  has_next: boolean;
  has_prev: boolean;
}

interface ServerSearchableDropdownProps<T> {
  // Function to fetch data from server
  fetchData: (search: string, page: number, perPage: number) => Promise<PaginatedResponse<T>>;
  // Current selected value
  value: string;
  // Callback when value changes (also passes the selected option for convenience)
  onChange: (value: string, selectedOption?: T) => void;
  // Get the value to store when an option is selected
  getOptionValue: (option: T) => string;
  // Get the display label for an option
  getOptionLabel: (option: T) => string;
  // Placeholder text
  placeholder?: string;
  // Is the dropdown disabled
  disabled?: boolean;
  // Debounce delay in ms
  debounceMs?: number;
  // Items per page
  perPage?: number;
  // Input size
  size?: 'sm' | 'lg';
  // Optional id for accessibility
  id?: string;
  // Optional refresh callback
  onRefresh?: () => void | Promise<void>;
  // Is refreshing
  isRefreshing?: boolean;
  // Show clear button
  showClearButton?: boolean;
  // Show match count
  showMatchCount?: boolean;
  // Aria label
  'aria-label'?: string;
  // Aria described by
  'aria-describedby'?: string;
}

// Constants
const MAX_DROPDOWN_HEIGHT = 300;
const DEFAULT_DEBOUNCE_MS = 300;
const DEFAULT_PER_PAGE = 20;

export default function ServerSearchableDropdown<T>({
  fetchData,
  value,
  onChange,
  getOptionValue,
  getOptionLabel,
  placeholder = 'Search...',
  disabled = false,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  perPage = DEFAULT_PER_PAGE,
  size,
  id,
  onRefresh,
  isRefreshing = false,
  showClearButton = true,
  showMatchCount = true,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
}: ServerSearchableDropdownProps<T>) {
  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Refs
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Fetch options from server
  const fetchOptions = useCallback(async (search: string, pageNum: number, append: boolean = false) => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchData(search, pageNum, perPage);

      if (append) {
        setOptions(prev => [...prev, ...response.items]);
      } else {
        setOptions(response.items);
      }

      setTotal(response.total);
      setPage(response.page);
      setHasMore(response.has_next);
      setHighlightedIndex(0);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return; // Ignore aborted requests
      }
      console.error('Error fetching options:', err);
      setError('Failed to load options');
      setOptions([]);
      setTotal(0);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  }, [fetchData, perPage]);

  // Debounced search
  const debouncedSearch = useCallback((search: string) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      fetchOptions(search, 1, false);
    }, debounceMs);
  }, [fetchOptions, debounceMs]);

  // Initial load when dropdown opens
  useEffect(() => {
    if (isOpen && options.length === 0 && !isLoading) {
      fetchOptions(searchTerm, 1, false);
    }
  }, [isOpen, options.length, isLoading, searchTerm, fetchOptions]);

  // Update selected label when value changes
  useEffect(() => {
    if (value && options.length > 0) {
      const selected = options.find(opt => getOptionValue(opt) === value);
      if (selected) {
        setSelectedLabel(getOptionLabel(selected));
      }
    } else if (!value) {
      setSelectedLabel('');
    }
  }, [value, options, getOptionValue, getOptionLabel]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(0);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Handle select
  const handleSelect = useCallback((option: T) => {
    const optionValue = getOptionValue(option);
    const optionLabel = getOptionLabel(option);
    onChange(optionValue, option);
    setSelectedLabel(optionLabel);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(0);
  }, [getOptionValue, getOptionLabel, onChange]);

  // Handle clear
  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onChange('');
    setSelectedLabel('');
    setSearchTerm('');
    setHighlightedIndex(0);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Handle refresh
  const handleRefresh = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onRefresh) {
      await onRefresh();
    }
    // Reload current search
    await fetchOptions(searchTerm, 1, false);
  };

  // Handle scroll to load more
  const handleScroll = () => {
    if (!listRef.current || isLoading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      // Near bottom, load more
      fetchOptions(searchTerm, page + 1, true);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
      setHighlightedIndex(0);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (options.length > 0 && highlightedIndex < options.length) {
        handleSelect(options[highlightedIndex]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      } else {
        setHighlightedIndex(prev =>
          prev < options.length - 1 ? prev + 1 : prev
        );
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      } else {
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0));
      }
    } else if (e.key === 'Home') {
      e.preventDefault();
      setHighlightedIndex(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setHighlightedIndex(Math.max(0, options.length - 1));
    }
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setHighlightedIndex(0);
    if (!isOpen) {
      setIsOpen(true);
    }
    debouncedSearch(newValue);
  };

  const hasValue = value && value.length > 0;

  return (
    <>
      <style>
        {`
          @keyframes server-dropdown-spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .server-dropdown-clear-btn {
            border: none;
            background: transparent;
            padding: 0 8px;
            color: #6c757d;
            cursor: pointer;
            display: flex;
            align-items: center;
          }
          .server-dropdown-clear-btn:hover {
            color: #dc3545;
          }
        `}
      </style>
      <div ref={dropdownRef} className="position-relative">
        <InputGroup size={size}>
          <Form.Control
            ref={inputRef}
            type="text"
            value={isOpen ? searchTerm : selectedLabel}
            onChange={handleInputChange}
            onFocus={() => {
              setIsOpen(true);
              setSearchTerm('');
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || isRefreshing}
            id={id}
            aria-label={ariaLabel}
            aria-describedby={ariaDescribedBy}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-activedescendant={
              isOpen && options.length > 0
                ? `option-${getOptionValue(options[highlightedIndex])}`
                : undefined
            }
          />
          {/* Clear button */}
          {showClearButton && hasValue && !disabled && !isOpen && (
            <button
              type="button"
              className="server-dropdown-clear-btn"
              onClick={handleClear}
              title="Clear selection"
              aria-label="Clear selection"
            >
              <i className="bi bi-x-lg"></i>
            </button>
          )}
          {/* Refresh button */}
          {onRefresh && (
            <Button
              variant="outline-secondary"
              onClick={handleRefresh}
              disabled={disabled || isLoading || isRefreshing}
              title="Refresh options"
              aria-label="Refresh options"
            >
              <i
                className="bi bi-arrow-clockwise"
                style={
                  isRefreshing
                    ? {
                        display: 'inline-block',
                        animation: 'server-dropdown-spin 1s linear infinite',
                      }
                    : undefined
                }
              ></i>
            </Button>
          )}
        </InputGroup>

        {isOpen && (
          <div
            className="position-absolute w-100 bg-white border rounded shadow-lg"
            style={{
              zIndex: 1000,
              marginTop: '2px',
              top: '100%',
              overflow: 'hidden',
            }}
            role="listbox"
          >
            {/* Match count display */}
            {showMatchCount && (
              <div
                className="px-3 py-1 text-muted small border-bottom d-flex justify-content-between align-items-center"
                style={{ backgroundColor: '#f8f9fa' }}
              >
                <span>
                  {isLoading ? 'Loading...' : `${total} items`}
                  {searchTerm && ` matching "${searchTerm}"`}
                </span>
                {isLoading && <Spinner animation="border" size="sm" />}
              </div>
            )}

            {/* Error display */}
            {error && (
              <div className="px-3 py-2 text-danger small">
                {error}
              </div>
            )}

            {/* Options list */}
            <div
              ref={listRef}
              style={{ maxHeight: `${MAX_DROPDOWN_HEIGHT}px`, overflowY: 'auto' }}
              onScroll={handleScroll}
            >
              {options.length === 0 && !isLoading ? (
                <div className="p-3 text-muted text-center">
                  {searchTerm ? 'No matches found' : 'No options available'}
                </div>
              ) : (
                options.map((option, index) => {
                  const optionValue = getOptionValue(option);
                  const optionLabel = getOptionLabel(option);
                  const isSelected = value === optionValue;
                  const isHighlighted = index === highlightedIndex;

                  return (
                    <div
                      key={optionValue}
                      id={`option-${optionValue}`}
                      role="option"
                      aria-selected={isSelected}
                      style={{
                        cursor: 'pointer',
                        padding: '8px 12px',
                        backgroundColor: isSelected
                          ? '#0d6efd'
                          : isHighlighted
                          ? '#e9ecef'
                          : undefined,
                        color: isSelected ? 'white' : undefined,
                        borderBottom: '1px solid #f0f0f0',
                      }}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onClick={() => handleSelect(option)}
                    >
                      {optionLabel}
                    </div>
                  );
                })
              )}
              {/* Loading indicator at bottom for pagination */}
              {isLoading && options.length > 0 && (
                <div className="p-2 text-center">
                  <Spinner animation="border" size="sm" />
                </div>
              )}
              {/* Load more indicator */}
              {hasMore && !isLoading && (
                <div className="p-2 text-center text-muted small">
                  Scroll for more...
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
